<template>
  <div class="date-slider-wrapper">
    <div class="date-slider" ref="slider">
      <button
        v-for="day in days"
        :key="day.dateStr"
        class="date-item"
        :class="{
          'date-item--active': day.dateStr === selectedDate,
          'date-item--today': day.isToday
        }"
        @click="selectDate(day.dateStr)"
      >
        <span class="date-item__weekday">{{ day.weekday }}</span>
        <span class="date-item__day">{{ day.day }}</span>
        <span class="date-item__month" v-if="day.showMonth">{{ day.month }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { formatLocalDate } from '@/utils/date'

export default {
  name: 'DateSlider',
  props: {
    value: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      selectedDate: this.value || this.getTodayStr(),
      days: []
    }
  },
  created() {
    this.buildDays()
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSelected()
    })
  },
  methods: {
    getTodayStr() {
      return formatLocalDate(new Date())
    },
    buildDays() {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const weekdays = ['日', '一', '二', '三', '四', '五', '六']
      const days = []

      for (let i = -5; i <= 5; i++) {
        const d = new Date(today)
        d.setDate(today.getDate() + i)
        const dateStr = formatLocalDate(d)
        const isToday = i === 0
        // Show month label when date is 1st or it's the first item
        const showMonth = d.getDate() === 1 || i === -5

        days.push({
          dateStr,
          day: d.getDate(),
          month: `${d.getMonth() + 1}月`,
          weekday: isToday ? '今' : weekdays[d.getDay()],
          isToday,
          showMonth
        })
      }

      this.days = days
    },
    selectDate(dateStr) {
      this.selectedDate = dateStr
      this.$emit('input', dateStr)
      this.$emit('change', dateStr)
    },
    scrollToSelected() {
      const slider = this.$refs.slider
      if (!slider) return
      const activeEl = slider.querySelector('.date-item--active')
      if (activeEl) {
        const sliderRect = slider.getBoundingClientRect()
        const itemRect = activeEl.getBoundingClientRect()
        const scrollLeft = activeEl.offsetLeft - (sliderRect.width / 2) + (itemRect.width / 2)
        slider.scrollTo({ left: scrollLeft, behavior: 'smooth' })
      }
    }
  },
  watch: {
    value(val) {
      if (val) this.selectedDate = val
    }
  }
}
</script>

<style scoped>
.date-slider-wrapper {
  background: var(--bg-card);
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 60px;
  z-index: 90;
}

.date-slider {
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding: 8px 12px;
  gap: 4px;
}
.date-slider::-webkit-scrollbar { display: none; }

.date-item {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  padding: 6px 4px;
  border-radius: var(--radius-md);
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--text-secondary);
  transition: var(--transition);
  scroll-snap-align: center;
  position: relative;
}

.date-item:hover {
  background: var(--bg-overlay);
  color: var(--text-primary);
}

.date-item--active {
  background: var(--primary);
  color: #fff;
  box-shadow: 0 4px 16px rgba(108,99,255,0.4);
}

.date-item--active:hover {
  background: var(--primary-light);
}

.date-item--today:not(.date-item--active) .date-item__day {
  color: var(--accent);
}

.date-item__weekday {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.03em;
  line-height: 1;
  margin-bottom: 4px;
}

.date-item__day {
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
}

.date-item__month {
  font-size: 10px;
  margin-top: 3px;
  opacity: 0.7;
}
</style>
